import React from 'react';
import PropTypes from 'prop-types';
import { defineMessages, FormattedMessage, injectIntl } from 'react-intl';
import FileIcon from '@/shared/components/FileIcon';
import intlShape from '@/shared/utils/intlShape';
import DocumentService from '@/shared/services/DocumentService';
import FormattedListValue from '@/shared/components/FormattedListValue';
import Button from '@/shared/components/Button';
import { CanDelete } from '@/shared/components/security';
import { MdForwardToInbox } from 'react-icons/md';
import { FaRedo } from 'react-icons/fa';

const messages = defineMessages({
  Forward: 'Forward',
  From: 'From',
  EmailTo: 'To',
  Cc: 'Cc',
  Bcc: 'Bcc',
  ReplyTo: 'Reply to',
  Subject: 'Subject',
  IncomingEmail: 'Incoming email',
  OutgoingEmail: 'Outgoing email',
  Attachments: 'Attachments',
  Download: 'Download',
  Status: 'Status',
  Reason: 'Reason',
  ResendEmail: 'Resend email',
  DeleteAttachment: 'Delete attachment',
});

const EmailMetadataDetails = ({
  metadataJson,
  documentType,
  attachments,
  onResendEmail,
  intl,
  onDeleteAttachment,
  onForward,
}) => {
  if (!metadataJson) return null;
  const email = typeof metadataJson === 'string' ? JSON.parse(metadataJson) : metadataJson;

  return (
    <div className="margin10T">
      <div className="row">
        <div className="col-md-12">
          <b>
            {email.direction === 0 && <FormattedMessage {...messages.IncomingEmail} />}
            {email.direction === 1 && <FormattedMessage {...messages.OutgoingEmail} />}
          </b>
        </div>
      </div>
      <div className="row margin5T">
        <div className="col-md-4 col-sm-6">
          <label>
            <b>
              <FormattedMessage {...messages.From} />
            </b>
          </label>
        </div>
        <div className="col-md-8 col-sm-6">
          <span style={{ wordWrap: 'break-word' }}>{email.from}</span>
        </div>
      </div>
      <div className="row margin5T">
        <div className="col-md-4 col-sm-6">
          <label>
            <b>
              <FormattedMessage {...messages.EmailTo} />
            </b>
          </label>
        </div>
        <div className="col-md-8 col-sm-6">
          <span style={{ wordWrap: 'break-word' }}>{email.to}</span>
        </div>
      </div>
      {email.cc && (
        <div className="row margin5T">
          <div className="col-md-4 col-sm-6">
            <label>
              <b>
                <FormattedMessage {...messages.Cc} />
              </b>
            </label>
          </div>
          <div className="col-md-8 col-sm-6">
            <span style={{ wordWrap: 'break-word' }}>{email.cc}</span>
          </div>
        </div>
      )}
      {email.bcc && (
        <div className="row margin5T">
          <div className="col-md-4 col-sm-6">
            <label>
              <b>
                <FormattedMessage {...messages.Bcc} />
              </b>
            </label>
          </div>
          <div className="col-md-8 col-sm-6">
            <span style={{ wordWrap: 'break-word' }}>{email.bcc}</span>
          </div>
        </div>
      )}
      {email.replyTo && (
        <div className="row margin5T">
          <div className="col-md-4 col-sm-6">
            <label>
              <b>
                <FormattedMessage {...messages.ReplyTo} />
              </b>
            </label>
          </div>
          <div className="col-md-8 col-sm-6">
            <span style={{ wordWrap: 'break-word' }}>{email.replyTo}</span>
          </div>
        </div>
      )}

      <div className="row margin5T">
        <div className="col-md-4 col-sm-6">
          <label>
            <b>
              <FormattedMessage {...messages.Subject} />
            </b>
          </label>
        </div>
        <div className="col-md-8 col-sm-6">{email.subject}</div>
      </div>
      {email.status > 0 && (
        <div className="row margin5T">
          <div className="col-md-4 col-sm-6">
            <label>
              <b>
                <FormattedMessage {...messages.Status} />
              </b>
            </label>
          </div>
          <div className="col-md-8 col-sm-6">
            <span className="text-nowrap">
              <span className="fa-md">
                {email.status === 2 && <i className="fa fa-info text-info green" />}
                {email.status === 3 && <i className="fa fa-eye text-info green" />}
                {email.status === 4 && <i className="fa fa-info text-info red" />}
              </span>
              <span className="margin5L">
                <FormattedListValue list="emailstatus" id={email.status} />
              </span>
            </span>
          </div>
        </div>
      )}
      {email.status === 4 && email.reason && (
        <div className="row margin5T">
          <div className="col-md-4 col-sm-6">
            <label>
              <b>
                <FormattedMessage {...messages.Reason} />
              </b>
            </label>
          </div>
          <div className="col-md-8 col-sm-6">
            <span>{email.reason}</span>
          </div>
        </div>
      )}
      {documentType === 8 && email.direction === 1 && (
        <div className="row margin5T">
          <div className="col-md-8 col-sm-6 col-md-offset-4 col-sm-offset-6">
            <Button onClick={() => onResendEmail()} showProgress btnClassName="btn-xs">
              <FaRedo className="react-icon margin5R" />
              <FormattedMessage {...messages.ResendEmail} />
            </Button>
          </div>
        </div>
      )}

      <div className="row margin5T">
        <div className="col-md-8 col-sm-6 col-md-offset-4 col-sm-offset-6">
          <Button onClick={() => onForward()} showProgress btnClassName="btn-xs">
            <MdForwardToInbox className="react-icon margin5R" />
            <FormattedMessage {...messages.Forward} />
          </Button>
        </div>
      </div>
      {attachments && attachments.length > 0 && (
        <div className="row margin5T">
          <div className="col-md-4 col-sm-6">
            <label>
              <b>
                <FormattedMessage {...messages.Attachments} />
              </b>
            </label>
          </div>
          <div className="col-md-8 col-sm-6">
            {attachments.map(attachment => (
              <span className="inline-email-attachment" style={{ padding: '0' }}>
                <span>
                  <a
                    onClick={() => DocumentService.open(attachment.id)}
                    title={intl.formatMessage(messages.Download)}
                  >
                    <FileIcon document={attachment} />
                    {attachment.name}
                  </a>
                </span>
                <CanDelete on="documents">
                  <i
                    onClick={() => onDeleteAttachment(attachment)}
                    title={intl.formatMessage(messages.DeleteAttachment)}
                    className="fa fa-trash-o clickable margin10L"
                  />
                </CanDelete>
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

EmailMetadataDetails.propTypes = {
  metadataJson: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({})]).isRequired,
  onResendEmail: PropTypes.func.isRequired,
  documentType: PropTypes.number.isRequired,
  intl: intlShape.isRequired,
};

export default injectIntl(EmailMetadataDetails);
